'use client';

/** @jsxImportSource @emotion/react */
import {
  Card,
  Chip,
  FlexItem,
  FlexLayout,
  Icon,
  IconContent,
  Image,
  responsive,
  ScrollLock,
  Typography,
} from '@wci-tech/core-ui';
import { css } from '@emotion/react';
import { FaArrowDownLong } from 'react-icons/fa6';
import { useTranslations } from 'next-intl';
import { useStyles } from './WhatWeProvide.styles';
import { FaCheck } from 'react-icons/fa';
import { BookADemoButton } from '@/components';
import { appConfig } from '@/config/appConfig';
import { ExplorePlatformButton } from '@/components';
const WhatWeProvide = () => {
  const styles = useStyles();
  const t = useTranslations('WhatWeProvide');
  // Logic to handle translated arrays/objects
  const whyDifferentKeys = ['item1', 'item2', 'item3'] as const;
  const outcomeKeys = ['item1', 'item2', 'item3', 'item4', 'item5'] as const;

  // Shared components/values
  const commonChip = (
    <Chip
      label={t('common.chipLabel')}
      enableHoverEffect={false}
      customStyles={{
        chipContainer: styles.titleChipContainer,
        label: styles.titleChipLabel,
      }}
    />
  );

  const ActionButtons = (
    <FlexLayout
      direction={{ default: 'column', md: 'row' }}
      justifyContent="center"
      gap={{ default: 20, md: 16 }}
      padding={{ default: 0 }}
      css={styles.buttonContainer}
    >
      <FlexItem>
        <BookADemoButton
          effect="three-d"
          shape="pill"
          css={styles.bookDemoButton}
          buttonText={t('common.bookDemo')}
          calendlyUrl={appConfig.integrations.calendly.eventUrl}
        />
      </FlexItem>
      <FlexItem>
        <ExplorePlatformButton />
      </FlexItem>
    </FlexLayout>
  );

  const cardBackgrounds = [
    'linear-gradient(118.1deg, #4054D1 19.24%, #212B6B 131.65%)',
    'linear-gradient(118.1deg, #4054D1 19.24%, #212B6B 131.65%)',
    'linear-gradient(118.1deg, #4054D1 19.24%, #212B6B 131.65%)',
    'linear-gradient(118.1deg, #4054D1 19.24%, #212B6B 131.65%)',
    'linear-gradient(118.1deg, #4054D1 19.24%, #212B6B 131.65%)',
  ];

  const renderCardWrapper = (content: React.ReactNode) => (
    <Card
      appearance="basic"
      customStyles={{
        container: css`
          background: transparent;
          border: none;
          box-shadow: none;
          width: 100%;
        `,
      }}
    >
      {content}
    </Card>
  );

  // --- CARD CONTENTS ---
  const CardOneContent = (
    <FlexLayout
      direction="column"
      alignItems="center"
      gap={{ default: 0 }}
      padding={{ default: 0 }}
    >
      <FlexItem css={styles.cardChipContainer}>{commonChip}</FlexItem>
      <FlexItem>
        <FlexLayout
          css={{ marginBottom: '16px' }}
          direction="column"
          alignItems="center"
          gap={{ default: 4, md: 12, lg: 16 }}
          padding={{ default: 0 }}
        >
          <Typography variant="h2" customStyles={styles.cardTitle}>
            {t('cardOne.title')}
          </Typography>

          <Typography
            variant="p"
            customStyles={[styles.cardDescription, styles.cardOneDescription]}
          >
            {t('cardOne.description')}
          </Typography>
        </FlexLayout>
      </FlexItem>
      <FlexItem>
        <Chip
          label={t('cardOne.subChip')}
          enableHoverEffect={false}
          customStyles={{
            chipContainer: styles.descriptionChipContainer,
            label: styles.descriptionChipLabel,
          }}
        />
      </FlexItem>
      <FlexItem css={styles.cardArrowContainer}>
        <Icon name={FaArrowDownLong} size={40} color="white" />
      </FlexItem>
      <FlexItem css={styles.cardOneImageContainer}>
        <Image
          isImgDecorative={false}
          src="/images/whatWeProvideCard1Mobile.png"
          altText={t('cardOne.title')}
          width={{ default: '100%', md: '610px', xxl: '859px' }}
          objectFit="contain"
          aspectRatio={{
            default: '750 / 420',
            md: '1220 / 674',
            xl: '1718 / 948',
          }} // original (width / height) = 1718 / 948 = 1.8, 1.8 is the aspect ratio of an image
          sizes="(max-width: 767px) 100vw, (max-width: 1399px) 610px, 859px"
          sources={[
            {
              srcSet: '/images/whatWeProvideCard1Desktop.png',
              media: '(min-width: 1280px)',
              type: 'image/png',
            },
            {
              srcSet: '/images/whatWeProvideCard1Tablet.png',
              media: '(min-width: 768px) and (max-width: 1279px)',
              type: 'image/png',
            },
            {
              srcSet: '/images/whatWeProvideCard1Tablet.png',
              media: '(max-width: 767px)',
              type: 'image/png',
            },
          ]}
        />
      </FlexItem>
      <FlexItem>
        <Typography variant="p" css={styles.firstCardfooterText}>
          {t('cardOne.footer')}
        </Typography>
      </FlexItem>
    </FlexLayout>
  );

  const CardTwoContent = (
    <FlexLayout
      direction="column"
      alignItems="center"
      gap={{ default: 0 }}
      padding={{ default: 0 }}
    >
      <FlexItem css={styles.cardChipContainer}>{commonChip}</FlexItem>
      <FlexItem>
        <FlexLayout
          direction="column"
          alignItems="center"
          gap={{ default: 4, md: 12, lg: 16 }}
          padding={{ default: 0 }}
        >
          <Typography variant="h2" customStyles={styles.cardTitle}>
            {t('cardTwo.title')}
          </Typography>
          <Typography
            variant="p"
            customStyles={[styles.cardDescription, styles.cardTwoDescription]}
          >
            {t('cardTwo.description')}
          </Typography>
        </FlexLayout>
      </FlexItem>
      <FlexItem
        customStyles={{
          flexItem: css`
            ${responsive.create({
              marginTop: { default: '16px', md: '36px' },
            })}
          `,
        }}
        css={styles.imageContainer}
      >
        <Image
          isImgDecorative={false}
          src="/images/analyticsMobile.png"
          altText={t('cardTwo.title')}
          width={{ default: '100%', md: '610px', xl: '859px' }}
          objectFit="contain"
          aspectRatio={{
            default: '580 / 342',
            md: '1283 / 646',
            xl: '1804 / 808',
          }}
          sizes="(max-width: 767px) 100vw, (max-width: 1399px) 610px, 859px"
          sources={[
            {
              srcSet: '/images/analyticsDesktop.png',
              media: '(min-width: 1280px)',
              type: 'image/png',
            },
            {
              srcSet: '/images/analyticsTablet.png',
              media: '(min-width: 768px) and (max-width: 1279px)',
              type: 'image/png',
            },
            {
              srcSet: '/images/analyticsTablet.png',
              media: '(max-width: 767px)',
              type: 'image/png',
            },
          ]}
        />
      </FlexItem>
      <FlexItem>
        <Typography variant="p" customStyles={styles.footerText}>
          {t('cardTwo.footer')}
        </Typography>
      </FlexItem>
      <FlexItem css={styles.fullWidthMobile}>{ActionButtons}</FlexItem>
    </FlexLayout>
  );

  const CardThreeContent = (
    <FlexLayout
      direction="column"
      alignItems="center"
      gap={{ default: 0 }}
      padding={{ default: 0 }}
    >
      <FlexItem css={styles.cardChipContainer}>{commonChip}</FlexItem>
      <FlexItem>
        <FlexLayout
          direction="column"
          alignItems="center"
          gap={{ default: 4, md: 12, lg: 16 }}
          padding={{ default: 0 }}
        >
          <Typography variant="h2" customStyles={styles.cardTitle}>
            {t('cardThree.title')}
          </Typography>
          <Typography variant="p" customStyles={styles.cardDescription}>
            {t('cardThree.description')}
          </Typography>
        </FlexLayout>
      </FlexItem>
      <FlexItem>
        <Typography variant="p" customStyles={styles.cardThreebodyText}>
          {t('cardThree.body')}
        </Typography>
      </FlexItem>
      <FlexItem
        customStyles={{
          flexItem: css`
            ${responsive.create({
              marginTop: { default: '16px', md: '0px', lg: '0px' },
            })}
          `,
        }}
        css={styles.imageContainer}
      >
        <Image
          isImgDecorative={false}
          src="/images/dataConsumptionMobile.png"
          altText={t('cardThree.title')}
          width={{ default: '100%', md: '600px', xxl: '745px' }}
          objectFit="cover"
          aspectRatio={{
            default: '320 / 108',
            md: '600 / 203',
            xl: '745 / 220',
          }}
          sizes="(max-width: 767px) 100vw, (max-width: 1399px) 610px, 859px"
          sources={[
            {
              srcSet: '/images/dataConsumptionDesktop.png',
              media: '(min-width: 1280px)',
              type: 'image/png',
            },
            {
              srcSet: '/images/dataConsumptionDesktop.png',
              media: '(min-width: 768px) and (max-width: 1279px)',
              type: 'image/png',
            },
            {
              srcSet: '/images/dataConsumptionDesktop.png',
              media: '(max-width: 767px)',
              type: 'image/png',
            },
          ]}
          borderRadius="12px"
        />
      </FlexItem>
      <FlexItem
        css={{ width: '100%' }}
        customStyles={{
          flexItem: styles.contentBox,
        }}
      >
        <FlexLayout
          direction={{ default: 'column', md: 'row' }}
          columns={12}
          padding={{ default: 0 }}
          gap={16}
          alignItems="stretch"
          justifyContent="center"
          customStyles={{ flexContainer: styles.infoBoxContainer }}
        >
          <FlexItem columnSpan={{ default: 12, md: 7 }} css={styles.whyDifferentColumn}>
            <div css={styles.infoBox}>
              <Typography variant="h3" customStyles={styles.boxTitle}>
                {t('cardThree.whyDifferentTitle')}
              </Typography>
              <div css={styles.iconListWrapper}>
                {whyDifferentKeys.map((key) => (
                  <IconContent
                    key={key}
                    withWrap={true}
                    iconVerticalAlignment="start"
                    icon={
                      <Icon
                        name={FaCheck}
                        color="#f5f6fa"
                        size={{
                          default: 10,
                          md: 12,
                          lg: 16,
                        }}
                      />
                    }
                    customStyles={{
                      left: styles.iconContentLeftIcon,
                      base: styles.outcomeContainer,
                    }}
                  >
                    <Typography variant="p" customStyles={styles.listText}>
                      <strong style={{ color: '#f5f6fa' }}>
                        {t(`cardThree.whyDifferent.${key}.title`)}
                      </strong>{' '}
                      — {t(`cardThree.whyDifferent.${key}.desc`)}
                    </Typography>
                  </IconContent>
                ))}
              </div>
            </div>
          </FlexItem>
          <FlexItem columnSpan={{ default: 12, md: 5 }}>
            <div css={styles.infoBox}>
              <Typography variant="h3" customStyles={styles.boxTitleOutcome}>
                {t('cardThree.outcomeTitle')}
              </Typography>
              <div css={styles.outcomeIconListWrapper}>
                {outcomeKeys.map((key) => (
                  <IconContent
                    key={key}
                    withWrap={true}
                    iconVerticalAlignment="center"
                    icon={
                      <Icon
                        name={FaCheck}
                        size={{
                          default: 10,
                          md: 12,
                          lg: 16,
                        }}
                        color="#17CECA"
                      />
                    }
                    customStyles={{
                      left: styles.iconContentLeftIcon,
                      base: styles.outcomeContainer,
                    }}
                  >
                    <Typography variant="p" customStyles={styles.listText}>
                      <span className="desktop-text">{t(`cardThree.outcomes.${key}.desktop`)}</span>
                      <span className="mobile-text">{t(`cardThree.outcomes.${key}.mobile`)}</span>
                    </Typography>
                  </IconContent>
                ))}
              </div>
            </div>
          </FlexItem>
        </FlexLayout>
      </FlexItem>
      <FlexItem css={styles.fullWidthMobile}>{ActionButtons}</FlexItem>
    </FlexLayout>
  );

  const CardFourContent = (
    <FlexLayout
      direction="column"
      alignItems="center"
      gap={{ default: 0 }}
      padding={{ default: 0 }}
    >
      <FlexItem css={styles.cardChipContainer}>{commonChip}</FlexItem>
      <FlexItem>
        <FlexLayout
          direction="column"
          alignItems="center"
          gap={{ default: 4, md: 12, lg: 16 }}
          padding={{ default: 0 }}
        >
          <Typography variant="h2" customStyles={styles.cardTitle}>
            {t('cardFour.title')}
          </Typography>
          <Typography variant="p" customStyles={styles.cardDescription}>
            {t('cardFour.description')}
          </Typography>
        </FlexLayout>
      </FlexItem>
      <FlexItem>
        <Typography variant="p" customStyles={styles.cardThreebodyText}>
          {t('cardFour.body')}
        </Typography>
      </FlexItem>
      <FlexItem
        customStyles={{
          flexItem: css`
            ${responsive.create({
              marginTop: { default: '16px', md: '0px', lg: '0px' },
            })}
          `,
        }}
        css={styles.imageContainer}
      >
        <Image
          isImgDecorative={false}
          src="/images/auditsTablet.png"
          altText={t('cardFour.title')}
          width={{ default: '100%', md: '600px', xl: '745px' }}
          objectFit="cover"
          aspectRatio={{
            default: '600 / 203',
            md: '600 / 203',
            xl: '745 / 220',
          }}
          sizes="(max-width: 767px) 100vw, (max-width: 1399px) 610px, 859px"
          sources={[
            {
              srcSet: '/images/auditsDesktop.png',
              media: '(min-width: 1400px)',
              type: 'image/png',
            },
            {
              srcSet: '/images/auditsTablet.png',
              media: '(min-width: 768px)',
              type: 'image/png',
            },
            {
              srcSet: '/images/auditsTablet.png',
              media: '(max-width: 767px)',
              type: 'image/png',
            },
          ]}
          borderRadius="12px"
        />
      </FlexItem>
      <FlexItem
        css={{ width: '100%' }}
        customStyles={{
          flexItem: styles.contentBox,
        }}
      >
        <FlexLayout
          direction={{ default: 'column', md: 'row' }}
          columns={12}
          padding={{ default: 0 }}
          gap={24}
          alignItems="stretch"
          justifyContent="center"
          customStyles={{ flexContainer: styles.infoBoxContainer }}
        >
          <FlexItem columnSpan={{ default: 12, md: 6 }} css={styles.problemsColumn}>
            <div css={styles.infoBox}>
              <Typography variant="h3" customStyles={styles.boxTitle}>
                {t('cardFour.problemsTitle')}
              </Typography>
              <div css={styles.iconListWrapper}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <IconContent
                    key={i}
                    withWrap
                    iconVerticalAlignment={{ default: 'center', md: 'center', lg: 'start' }}
                    icon={
                      <Icon
                        name={FaCheck}
                        size={{
                          default: 10,
                          md: 12,
                          lg: 16,
                        }}
                        color="red"
                      />
                    }
                    customStyles={{
                      left: styles.iconContentLeftIcon,
                      base: styles.outcomeContainer,
                    }}
                  >
                    <Typography variant="p" customStyles={styles.listText}>
                      {t(`cardFour.problems.${i}`)}
                    </Typography>
                  </IconContent>
                ))}
              </div>
            </div>
          </FlexItem>
          <FlexItem columnSpan={{ default: 12, md: 6 }}>
            <div css={styles.infoBox}>
              <Typography variant="h3" customStyles={styles.boxTitleOutcome}>
                {t('cardFour.outcomeTitle')}
              </Typography>
              <div css={styles.iconListWrapper}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <IconContent
                    key={i}
                    withWrap
                    iconVerticalAlignment={{ default: 'center', md: 'center', lg: 'start' }}
                    icon={
                      <Icon
                        name={FaCheck}
                        size={{
                          default: 10,
                          md: 12,
                          lg: 16,
                        }}
                        color="#17CECA"
                      />
                    }
                    customStyles={{
                      left: styles.iconContentLeftIcon,
                      base: styles.outcomeContainer,
                    }}
                  >
                    <Typography variant="p" customStyles={styles.listText}>
                      {t(`cardFour.outcomes.${i}`)}
                    </Typography>
                  </IconContent>
                ))}
              </div>
            </div>
          </FlexItem>
        </FlexLayout>
      </FlexItem>
      <FlexItem css={styles.fullWidthMobile}>{ActionButtons}</FlexItem>
    </FlexLayout>
  );

  const CardFiveContent = (
    <FlexLayout
      direction="column"
      alignItems="center"
      gap={{ default: 0 }}
      padding={{ default: 0 }}
    >
      <FlexItem css={styles.cardChipContainer}>{commonChip}</FlexItem>
      <FlexItem>
        <FlexLayout
          direction="column"
          alignItems="center"
          gap={{ default: 4, md: 12, lg: 16 }}
          padding={{ default: 0 }}
        >
          <Typography variant="h2" customStyles={styles.cardTitle}>
            {t('cardFive.title')}
          </Typography>
          <Typography variant="p" customStyles={styles.cardDescription}>
            {t('cardFive.description')}
          </Typography>
        </FlexLayout>
      </FlexItem>
      <FlexItem
        customStyles={{
          flexItem: css`
            ${responsive.create({ marginTop: { default: '20px', md: '30px' } })}
          `,
        }}
        css={styles.imageContainer}
      >
        <Image
          isImgDecorative={false}
          src="/images/deliveryMobile.png"
          altText={t('cardFive.title')}
          width={{ default: '100%', md: '600px', xl: '745px' }}
          aspectRatio={{
            default: '288 / 176',
            md: '514 / 318',
            xl: '722 / 380',
          }}
          sizes="(max-width: 767px) 100vw, (max-width: 1399px) 610px, 859px"
          objectFit="contain"
          sources={[
            {
              srcSet: '/images/deliveryDesktop.png',
              media: '(min-width: 1280px)',
              type: 'image/png',
            },
            {
              srcSet: '/images/deliveryTablet.png',
              media: '(min-width: 768px) and (max-width: 1279px)',
              type: 'image/png',
            },
            {
              srcSet: '/images/deliveryTablet.png',
              media: '(max-width: 767px)',
              type: 'image/png',
            },
          ]}
        />
      </FlexItem>
      <FlexItem>
        <Typography variant="p" customStyles={styles.footerText}>
          {t('cardFive.footer')}
        </Typography>
      </FlexItem>
      <FlexItem css={styles.fullWidthMobile}>{ActionButtons}</FlexItem>
    </FlexLayout>
  );

  const allCards = [
    renderCardWrapper(CardOneContent),
    renderCardWrapper(CardTwoContent),
    renderCardWrapper(CardThreeContent),
    renderCardWrapper(CardFourContent),
    renderCardWrapper(CardFiveContent),
  ];

  return (
    <section css={styles.wrapper} id="what-we-provide">
      <ScrollLock
        items={allCards}
        backgroundItems={cardBackgrounds}
        variant="snapping"
        customStyles={{
          scrollItem: styles.scrollTrack,
          dotsContainer: css`
            bottom: 36px;
          `,
        }}
      />
    </section>
  );
};

export default WhatWeProvide;
